import {
  ACCORDION_ROW_ACTIVE_CLASS,
  ACCORDION_WRAP_ACTIVE_CLASS,
} from '../shared/constants.js';
import { prefersReducedMotion } from '../shared/browser.js';

const getAccordionIndex = (progress, total) =>
  Math.min(total - 1, Math.floor(progress * total));

const isMobileAccordionLayout = () => window.matchMedia('(max-width: 767px)').matches;

const getAccordionTriggers = (rows) =>
  rows.map((row) => row.querySelector('.content__column--trigger'));

const getSegmentProgress = (index, total) => (index + 0.5) / total;

const scrollToSegment = (scrollTrigger, index, total) => {
  const start = scrollTrigger.start || 0;
  const end = scrollTrigger.end || start;
  const top = start + (end - start) * getSegmentProgress(index, total);

  window.scrollTo({
    top,
    behavior: prefersReducedMotion() ? 'auto' : 'smooth',
  });
};

const getBodyDatasetEntries = (row, nextIndex) => {
  const bodyDatasetEntries = {
    accordionIndex: String(nextIndex),
    accordionItem: String(nextIndex + 1),
  };

  Object.entries(row.dataset).forEach(([key, value]) => {
    if (key.startsWith('body') && key.length > 4) {
      const bodyKey = key.charAt(4).toLowerCase() + key.slice(5);

      bodyDatasetEntries[bodyKey] = value;
      return;
    }

    bodyDatasetEntries[key] = value;
  });

  return bodyDatasetEntries;
};

const syncBodyAttributes = (state, nextIndex) => {
  if (typeof document === 'undefined' || !document.body) {
    return;
  }

  const nextRow = state.rows[nextIndex];
  const nextEntries = getBodyDatasetEntries(nextRow, nextIndex);
  const nextThemeClass = nextRow.dataset.theme ? `is-${nextRow.dataset.theme}` : null;

  state.bodyDatasetKeys.forEach((key) => {
    if (!(key in nextEntries)) {
      delete document.body.dataset[key];
    }
  });

  Object.entries(nextEntries).forEach(([key, value]) => {
    document.body.dataset[key] = value;
  });

  if (state.bodyThemeClass) {
    document.body.classList.remove(state.bodyThemeClass);
  }

  if (nextThemeClass) {
    document.body.classList.add(nextThemeClass);
  }

  state.bodyDatasetKeys = Object.keys(nextEntries);
  state.bodyThemeClass = nextThemeClass;
};

const clearBodyAttributes = (state) => {
  if (typeof document === 'undefined' || !document.body) {
    return;
  }

  state.bodyDatasetKeys.forEach((key) => {
    delete document.body.dataset[key];
  });

  if (state.bodyThemeClass) {
    document.body.classList.remove(state.bodyThemeClass);
  }

  state.bodyDatasetKeys = [];
  state.bodyThemeClass = null;
};

const setAccordionIndex = (state, nextIndex) => {
  if (nextIndex === state.activeIndex) {
    return;
  }

  state.rows.forEach((row, index) => {
    row.classList.toggle(ACCORDION_ROW_ACTIVE_CLASS, index === nextIndex);
    row.setAttribute('aria-expanded', String(index === nextIndex));
  });

  if (state.disableActiveTrigger) {
    state.triggers.forEach((trigger, index) => {
      trigger?.setAttribute('aria-disabled', String(index === nextIndex));
    });
  }

  state.wraps.forEach((wrap, index) => {
    wrap.classList.toggle(ACCORDION_WRAP_ACTIVE_CLASS, index === nextIndex);
    wrap.setAttribute('aria-hidden', String(index !== nextIndex));
  });

  if (state.syncBodyAttributes && state.isInView) {
    syncBodyAttributes(state, nextIndex);
  }

  state.activeIndex = nextIndex;
};

const clearAccordionIndex = (state) => {
  state.rows.forEach((row) => {
    row.classList.remove(ACCORDION_ROW_ACTIVE_CLASS);
    row.setAttribute('aria-expanded', 'false');
  });

  state.wraps.forEach((wrap) => {
    wrap.classList.remove(ACCORDION_WRAP_ACTIVE_CLASS);
    wrap.setAttribute('aria-hidden', 'true');
  });

  if (state.disableActiveTrigger) {
    state.triggers.forEach((trigger) => {
      trigger?.setAttribute('aria-disabled', 'false');
    });
  }

  state.activeIndex = -1;
};

const initMobileAccordion = (state) => {
  state.triggers.forEach((trigger, index) => {
    if (!trigger) {
      return;
    }

    if (!['A', 'BUTTON'].includes(trigger.tagName)) {
      trigger.setAttribute('role', 'button');
      trigger.setAttribute('tabindex', '0');
    }

    trigger.addEventListener('click', () => {
      if (state.activeIndex === index) {
        clearAccordionIndex(state);
        return;
      }

      setAccordionIndex(state, index);
    });

    trigger.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }

      event.preventDefault();
      trigger.click();
    });
  });
};

const initDesktopAccordionControls = (state, scrollTrigger) => {
  state.triggers.forEach((trigger, index) => {
    if (!trigger) {
      return;
    }

    if (!['A', 'BUTTON'].includes(trigger.tagName)) {
      trigger.setAttribute('role', 'button');
      trigger.setAttribute('tabindex', '0');
    }

    trigger.addEventListener('click', (event) => {
      event.preventDefault();

      if (state.activeIndex === index) {
        return;
      }

      scrollToSegment(scrollTrigger, index, state.rows.length);
    });

    trigger.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }

      event.preventDefault();

      if (state.activeIndex !== index) {
        scrollToSegment(scrollTrigger, index, state.rows.length);
      }
    });
  });
};

export const initAccordion = (track, ScrollTrigger) => {
  if (track.dataset.accordionReady === 'true' || !ScrollTrigger) {
    return;
  }

  const rows = Array.from(track.querySelectorAll('.content__row--accordion'));

  if (rows.length < 2) {
    return;
  }

  const wraps = rows.map((row) => row.querySelector('.accordion__content-wrap'));

  if (wraps.some((wrap) => !wrap)) {
    return;
  }

  track.dataset.accordionReady = 'true';

  const initialIndex = Math.max(
    0,
    rows.findIndex((row) => row.classList.contains(ACCORDION_ROW_ACTIVE_CLASS))
  );
  const state = {
    activeIndex: -1,
    bodyDatasetKeys: [],
    bodyThemeClass: null,
    disableActiveTrigger: !isMobileAccordionLayout(),
    isInView: false,
    rows,
    syncBodyAttributes: !isMobileAccordionLayout(),
    triggers: getAccordionTriggers(rows),
    wraps,
  };

  setAccordionIndex(state, initialIndex);

  if (isMobileAccordionLayout()) {
    initMobileAccordion(state);
    return;
  }

  if (prefersReducedMotion()) {
    return;
  }

  const scrollTrigger = ScrollTrigger.create({
    trigger: track,
    start: 'top top',
    end: 'bottom bottom',
    invalidateOnRefresh: true,
    onRefresh: (self) => {
      state.isInView = self.isActive;
      setAccordionIndex(state, getAccordionIndex(self.progress, rows.length));

      if (!state.isInView) {
        clearBodyAttributes(state);
      }
    },
    onUpdate: (self) => {
      setAccordionIndex(state, getAccordionIndex(self.progress, rows.length));
    },
    onEnter: () => {
      state.isInView = true;
      syncBodyAttributes(state, state.activeIndex);
    },
    onEnterBack: () => {
      state.isInView = true;
      syncBodyAttributes(state, state.activeIndex);
    },
    onLeave: () => {
      state.isInView = false;
      clearBodyAttributes(state);
    },
    onLeaveBack: () => {
      state.isInView = false;
      clearBodyAttributes(state);
    },
  });

  initDesktopAccordionControls(state, scrollTrigger);
};
